import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import cogoToast from "cogo-toast";
import { SUCCESS_RESULT } from "../../Constants";
import { genericPost } from "../../services/Api";

const LIST_ENDPOINT = "unit4/project-mappings/list";
const UPSERT_ENDPOINT = "unit4/project-mappings/create_or_update";

const normalizeForMatch = (header) =>
  String(header || "")
    .toLowerCase()
    .replace(/\ufeff/g, "")
    .replace(/[_\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseNumberOrNull = (value) => {
  const text = String(value ?? "").replace(/,/g, "").replace(/%/g, "").trim();
  if (!text) return null;
  // Common spreadsheet placeholders should be treated as empty, not invalid
  if (["-", "--", "n/a", "na", "null", "nil"].includes(text.toLowerCase())) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const UploadProjectMappings = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [validRecords, setValidRecords] = useState([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [parseError, setParseError] = useState("");
  const [parseInfo, setParseInfo] = useState("");
  const [invalidReasonSummary, setInvalidReasonSummary] = useState([]);
  const [invalidRowSamples, setInvalidRowSamples] = useState([]);

  const listColumns = useMemo(() => {
    if (!rows.length || typeof rows[0] !== "object") return [];
    return Object.keys(rows[0]);
  }, [rows]);

  const loadMappings = () => {
    setIsLoading(true);
    genericPost(LIST_ENDPOINT, {})
      .then((res) => {
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setRows(Array.isArray(res?.data?.data) ? res.data.data : []);
          setMessage(res?.data?.message || "Request successful.");
          return;
        }
        setRows([]);
        setMessage(res?.data?.message || "Failed to load project mappings.");
        cogoToast.error(res?.data?.message || "Failed to load project mappings", { hideAfter: 4 });
      })
      .catch(() => {
        setRows([]);
        setMessage("Failed to load project mappings.");
        cogoToast.error("Failed to load project mappings", { hideAfter: 4 });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadMappings();
  }, []);

  const resetUploadState = () => {
    setFileName("");
    setValidRecords([]);
    setInvalidCount(0);
    setParseError("");
    setParseInfo("");
    setInvalidReasonSummary([]);
    setInvalidRowSamples([]);
  };

  const closeUploadModal = () => {
    if (isUploading) return;
    setIsUploadModalOpen(false);
    resetUploadState();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    resetUploadState();
    if (!file) return;

    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rowsAoA = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
      const headerRow = Array.isArray(rowsAoA[0]) ? rowsAoA[0] : [];
      const dataRows = rowsAoA.slice(1);

      if (!dataRows.length) {
        setParseError("Uploaded file has no data rows.");
        return;
      }

      const normalizedHeaders = headerRow.map((h) => normalizeForMatch(h));
      const findIndexes = (target) => {
        const t = normalizeForMatch(target);
        const indexes = [];
        normalizedHeaders.forEach((h, idx) => {
          if (h === t) indexes.push(idx);
        });
        return indexes;
      };

      const indexOrMinus1 = (target, occurrence = 0) => {
        const indexes = findIndexes(target);
        return indexes.length > occurrence ? indexes[occurrence] : -1;
      };

      const idx = {
        project: indexOrMinus1("Project"),
        workorder: indexOrMinus1("Workorder"),
        workord_name: indexOrMinus1("Workord_Name"),
        activity: indexOrMinus1("Activity"),
        activity_desc: indexOrMinus1("Activity_desc"),
        budget: indexOrMinus1("Budget"),
        actual: indexOrMinus1("Actual Expense"),
        expense: indexOrMinus1("Expense"),
        cash_advance: indexOrMinus1("Cash_advance"),
        total: indexOrMinus1("Total Expenses"),
        expenses: indexOrMinus1("Expenses"),
        total_commitments: indexOrMinus1("Total Commitments"),
        spent_1: indexOrMinus1("% SPENT", 0),
        spent_2: indexOrMinus1("% SPENT", 1),
        balance_1: indexOrMinus1("Balance", 0),
        balance_2: indexOrMinus1("Balance", 1),
        balance_percent_1: indexOrMinus1("Balance %", 0),
        balance_percent_2: indexOrMinus1("Balance %", 1),
      };

      const missingHeaders = Object.entries(idx)
        .filter(([, index]) => index < 0)
        .map(([name]) => name);

      if (missingHeaders.length) {
        setParseInfo(`Missing columns ignored: ${missingHeaders.join(", ")}`);
      }

      const getCell = (row, index) => (index >= 0 ? row[index] : "");

      const normalizedRows = dataRows.map((row, rowIndex) => {
        return {
          _rowNumber: rowIndex + 2,
          project: String(getCell(row, idx.project) || "").trim(),
          workorder: String(getCell(row, idx.workorder) || "").trim(),
          workord_name: String(getCell(row, idx.workord_name) || "").trim(),
          activity: String(getCell(row, idx.activity) || "").trim(),
          activity_desc: String(getCell(row, idx.activity_desc) || "").trim(),
          budget: parseNumberOrNull(getCell(row, idx.budget)),
          actual: parseNumberOrNull(getCell(row, idx.actual)),
          expense: parseNumberOrNull(getCell(row, idx.expense)),
          cash_advance: parseNumberOrNull(getCell(row, idx.cash_advance)),
          total: parseNumberOrNull(getCell(row, idx.total)),
          // fallback to second repeated columns in shared templates
          expenses:
            parseNumberOrNull(getCell(row, idx.expenses)) ??
            parseNumberOrNull(getCell(row, idx.spent_2)),
          spent: parseNumberOrNull(getCell(row, idx.spent_1)),
          balance:
            parseNumberOrNull(getCell(row, idx.balance_1)) ??
            parseNumberOrNull(getCell(row, idx.balance_2)),
          balance_persent:
            parseNumberOrNull(getCell(row, idx.balance_percent_1)) ??
            parseNumberOrNull(getCell(row, idx.balance_percent_2)),
          total_commitments: parseNumberOrNull(getCell(row, idx.total_commitments)),
        };
      });

      const invalidRows = [];
      const valid = normalizedRows.filter((row) => {
        const reasons = [];
        const hasAnyValue =
          row.project ||
          row.workorder ||
          row.workord_name ||
          row.activity ||
          row.activity_desc ||
          row.budget !== null ||
          row.actual !== null ||
          row.expense !== null ||
          row.cash_advance !== null ||
          row.total !== null ||
          row.expenses !== null ||
          row.spent !== null ||
          row.balance !== null ||
          row.balance_persent !== null ||
          row.total_commitments !== null;
        if (!hasAnyValue) reasons.push("empty row");

        const numericFields = [
          "budget",
          "actual",
          "expense",
          "cash_advance",
          "total",
          "expenses",
          "spent",
          "balance",
          "balance_persent",
          "total_commitments",
        ];
        const invalidNumericFields = numericFields.filter(
          (field) => row[field] !== null && !Number.isFinite(row[field])
        );
        if (invalidNumericFields.length) {
          reasons.push(`invalid numeric value in: ${invalidNumericFields.join(", ")}`);
        }

        const isValid = hasAnyValue && invalidNumericFields.length === 0;
        if (!isValid) {
          invalidRows.push({
            row: row._rowNumber,
            reason: reasons.join("; "),
          });
        }
        return isValid;
      });

      setValidRecords(valid);
      setInvalidCount(normalizedRows.length - valid.length);
      const summaryMap = {};
      invalidRows.forEach((entry) => {
        summaryMap[entry.reason] = (summaryMap[entry.reason] || 0) + 1;
      });
      setInvalidReasonSummary(
        Object.keys(summaryMap).map((reason) => ({
          reason,
          count: summaryMap[reason],
        }))
      );
      setInvalidRowSamples(invalidRows.slice(0, 8));

      if (!valid.length) {
        setParseError("No valid rows found. Check the invalid reasons shown below.");
      }
    } catch (err) {
      setParseError("Could not parse the uploaded file.");
    }
  };

  const uploadRecords = () => {
    if (!validRecords.length) {
      cogoToast.error("No valid records to upload", { hideAfter: 3 });
      return;
    }

    setIsUploading(true);
    genericPost(UPSERT_ENDPOINT, { records: validRecords })
      .then((res) => {
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          cogoToast.success(res?.data?.message || "Upload completed", { hideAfter: 4 });
          closeUploadModal();
          loadMappings();
          return;
        }
        cogoToast.error(res?.data?.message || "Upload failed", { hideAfter: 4 });
      })
      .catch(() => {
        cogoToast.error("Upload failed", { hideAfter: 4 });
      })
      .finally(() => setIsUploading(false));
  };

  return (
    <div style={{ padding: "20px", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <h2 style={{ marginBottom: 10 }}>Upload Project Mappings</h2>
      <Breadcrumb className="ml-1 mb-3">
        <BreadcrumbItem>
          <Link to="/dashboard">Dashboard</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Upload Project Mappings</BreadcrumbItem>
      </Breadcrumb>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
            <div className="text-muted">{message}</div>
            <div className="d-flex" style={{ gap: 8 }}>
              <button className="btn btn-outline-primary" onClick={() => setIsUploadModalOpen(true)}>
                Upload Project Mapping
              </button>
              <button className="btn btn-primary" onClick={loadMappings} disabled={isLoading}>
                {isLoading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover table-sm">
              <thead>
                <tr>
                  {listColumns.length === 0 ? <th>Data</th> : listColumns.map((col) => <th key={col}>{col}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !isLoading ? (
                  <tr>
                    <td className="text-center text-muted" colSpan={Math.max(listColumns.length, 1)}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row?.id || index}>
                      {listColumns.length === 0 ? (
                        <td>{JSON.stringify(row)}</td>
                      ) : (
                        listColumns.map((col) => <td key={`${index}-${col}`}>{row?.[col] ?? "-"}</td>)
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal isOpen={isUploadModalOpen} toggle={closeUploadModal} size="lg">
        <ModalHeader toggle={closeUploadModal}>Upload Project Mapping</ModalHeader>
        <ModalBody>
          <label className="font-weight-bold mb-2">Upload Excel File</label>
          <input
            type="file"
            className="form-control"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {fileName && <div className="small text-muted mt-2">File: {fileName}</div>}

          {!!parseError && (
            <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
              {parseError}
            </div>
          )}
          {!!parseInfo && !parseError && (
            <div className="alert alert-info py-2 mt-3 mb-0" role="status">
              {parseInfo}
            </div>
          )}

          <div className="mt-3 d-flex flex-wrap" style={{ gap: 8 }}>
            <span className="badge badge-success">Valid rows: {validRecords.length}</span>
            <span className="badge badge-warning">Invalid rows: {invalidCount}</span>
          </div>

          {invalidReasonSummary.length > 0 && (
            <div className="alert alert-warning mt-3 mb-0 py-2">
              <div className="font-weight-bold mb-1">Invalid reasons</div>
              {invalidReasonSummary.map((item) => (
                <div key={item.reason}>
                  {item.reason}: {item.count} row(s)
                </div>
              ))}
              {invalidRowSamples.length > 0 && (
                <div className="mt-2">
                  <div className="font-weight-bold">Sample rows</div>
                  {invalidRowSamples.map((sample, idx) => (
                    <div key={`${sample.row}-${idx}`}>Row {sample.row}: {sample.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-outline-secondary" onClick={closeUploadModal} disabled={isUploading}>
            Close
          </button>
          <button className="btn btn-success" onClick={uploadRecords} disabled={isUploading || !validRecords.length}>
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UploadProjectMappings;
